import React from 'react';
import { FileText, ExternalLink, AlertTriangle, CheckCircle } from 'lucide-react';

import { Report } from '../../lib/api/requests';

export type ReportReact = {
  url: string;
  results: ReportResult[];
};

export type ReportResult = {
  success: boolean;
  message?: string;
  data: Report[];
  url: string;
  issues: number;
};

interface IReportTableProps {
  fullReportUrl: string;
  report: ReportResult;
}

const ReportTable: React.FC<IReportTableProps> = ({ report, fullReportUrl }) => {
  const ensureProtocol = (url: string): string => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case '1 - critical':
        return '#dc2626'; // Red
      case '2 - serious':
        return '#ea580c'; // Orange
      case '3 - moderate':
        return '#ca8a04'; // Yellow
      case '4 - minor':
        return '#16a34a'; // Green
      default:
        return '#6b7280'; // Gray
    }
  };

  const getImpactDescription = (impact: string): string => {
    switch (impact) {
      case '1 - critical':
        return 'Critical impact - highest severity level';
      case '2 - serious':
        return 'Serious impact - high severity level';
      case '3 - moderate':
        return 'Moderate impact - medium severity level';
      case '4 - minor':
        return 'Minor impact - low severity level';
      default:
        return impact;
    }
  };

  return (
    <div className="report-table">
      <div className="report-table__header">
        <div className="report-table__url">
          <FileText className="report-table__icon" aria-hidden="true" />
          <span className="report-table__label">Page:</span>
          <a href={ensureProtocol(report.url)} className="report-table__link" target="_blank" rel="noopener noreferrer">
            {report.url}
            <ExternalLink className="report-table__external-icon" aria-hidden="true" />
          </a>
        </div>
      </div>

      {report.success ? (
        <div className="report-table__content">
          <div className="report-table__summary">
            <div className="report-table__issues">
              <AlertTriangle className="report-table__summary-icon" aria-hidden="true" />
              <span className="report-table__summary-text">
                <strong>Found violations:</strong> {report.issues}
              </span>
            </div>
          </div>

          <div className="report-table__table-container">
            <table className="report-table__table" aria-label="Accessibility violations table">
              <thead className="report-table__thead">
                <tr className="report-table__tr">
                  <th className="report-table__th">Impact</th>
                  <th className="report-table__th">Impact Level</th>
                  <th className="report-table__th">Count</th>
                  <th className="report-table__th">Description</th>
                </tr>
              </thead>
              <tbody className="report-table__tbody">
                {report.data.map((row) => (
                  <tr key={row.id} className="report-table__tr">
                    <td className="report-table__td">{row.impact}</td>
                    <td className="report-table__td">
                      <div className="report-table__impact-indicator">
                        <div 
                          className="report-table__impact-dot"
                          style={{ backgroundColor: getImpactColor(row.impact) }}
                          aria-hidden="true"
                        />
                        <span className="sr-only">{getImpactDescription(row.impact)}</span>
                      </div>
                    </td>
                    <td className="report-table__td">{row.nodes}</td>
                    <td className="report-table__td">{row.help}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="report-table__actions">
            <button
              className="report-table__button"
              onClick={() => window.open(fullReportUrl, '_blank')}
              aria-label="Open full accessibility report in a new window"
            >
              <FileText className="report-table__button-icon" aria-hidden="true" />
              Show Full Report
              <ExternalLink className="report-table__button-external" aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : (
        <div className="report-table__error">
          <div className="report-table__error-header">
            <AlertTriangle className="report-table__error-icon" aria-hidden="true" />
            <span className="report-table__error-title">Scan Error: Unable to Complete WCAG Analysis</span>
          </div>
          
          <div className="report-table__error-content">
            <div className="report-table__error-section">
              <strong>Reason:</strong> The webpage you are attempting to scan is protected by Cloudflare, and our tool
              is unable to access it for analysis. Cloudflare security settings are preventing automated scanning tools
              from accessing this page. This can occur when the page is protected against bots or unusual traffic.
            </div>
            
            <div className="report-table__error-section">
              <strong>Solution:</strong> Our team can perform a manual review of the webpage to ensure it meets WCAG
              standards.
            </div>
            
            <div className="report-table__error-section">
              Please reach out to us at{' '}
              <a 
                href="mailto:support@mnb.solutions?subject=Manual page review" 
                className="report-table__email-link"
              >
                support@mnb.solutions
              </a>{' '}
              to get started with the manual scan.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportTable;
